<div class="cmo-dash">
    <!-- Top bar -->
    <div class="cmo-topbar">
        <div class="cmo-topbar__left">
            <h2 class="cmo-topbar__title">
                <i class="fa-solid fa-brain"></i> AI CMO
                <span class="cmo-processing-dot" *ngIf="isRunning"></span>
            </h2>
            <!-- View toggle -->
            <div class="cmo-view-toggle">
                <button class="cmo-view-btn" [class.cmo-view-btn--active]="activeView === 'calendar'" (click)="activeView = 'calendar'">
                    <i class="fa-solid fa-calendar-days"></i> Calendar
                </button>
                <button class="cmo-view-btn" [class.cmo-view-btn--active]="activeView === 'review-queue'" (click)="activeView = 'review-queue'">
                    <i class="fa-solid fa-inbox"></i> Queue
                    <span class="cmo-view-btn__badge" *ngIf="needsReviewCount > 0">{{ needsReviewCount }}</span>
                </button>
                <button class="cmo-view-btn" [class.cmo-view-btn--active]="activeView === 'studio'" (click)="activeView = 'studio'">
                    <i class="fa-solid fa-diagram-project"></i> Studio
                </button>
                <button class="cmo-view-btn" [class.cmo-view-btn--active]="activeView === 'marketing-dashboard'" (click)="activeView = 'marketing-dashboard'">
                    <i class="fa-solid fa-bullhorn"></i> Marketing
                </button>
                <button class="cmo-view-btn" [class.cmo-view-btn--active]="activeView === 'jobs'" (click)="activeView = 'jobs'">
                    <i class="fa-solid fa-clock-rotate-left"></i> Jobs
                </button>
            </div>
        </div>
        <div class="cmo-topbar__right">
            <select [(ngModel)]="defaultCompanyId" class="cmo-select" (ngModelChange)="onCompanyChange()">
                <option value="" disabled>Select company</option>
                <option *ngFor="let c of companies" [value]="c.ID">{{ c.Name }}</option>
            </select>
            <button class="cmo-topbar__settings-btn" (click)="settingsOpen = true" title="Settings">
                <i class="fa-solid fa-gear"></i>
            </button>
        </div>
    </div>

    <!-- Loading -->
    <div class="cmo-loading" *ngIf="isLoading">
        <div class="cmo-spinner-bar"><div class="cmo-spinner-fill"></div></div>
        <span>Loading dashboard data...</span>
    </div>

    <!-- Main layout: content area + sidebar (hidden when settings open) -->
    <div class="cmo-layout" *ngIf="!isLoading && !settingsOpen">

        <!-- Main content area -->
        <div class="cmo-main">

            <!-- ═══════════════ CALENDAR VIEW ═══════════════ -->
            <div *ngIf="activeView === 'calendar'" class="cmo-view">
                <mkt-calendar-grid
                    [calendarEntries]="calendarEntries"
                    [blogs]="blogs"
                    [contentTypes]="contentTypes"
                    [contentStatuses]="contentStatuses"
                    [contentPlanItems]="contentPlanItems"
                    [viewMode]="calendarViewMode"
                    [currentDate]="calendarDate"
                    (entryClicked)="onCalendarEntryClicked($event)"
                    (entryEditRequested)="onCalendarEntryEdit($event)"
                    (dateClicked)="onCalendarDateClicked($event)"
                    (entryRescheduled)="onEntryRescheduled($event)"
                    (viewModeChanged)="calendarViewMode = $event"
                    (navigated)="calendarDate = $event">
                </mkt-calendar-grid>
            </div>

            <!-- ═══════════════ STUDIO VIEW ═══════════════════ -->
            <div *ngIf="activeView === 'studio'" class="cmo-view">
                <mkt-pipeline-composer
                    [companyId]="defaultCompanyId"
                    [blogs]="blogs"
                    [contentSources]="contentSources"
                    [contentTemplates]="contentTemplates"
                    (agentCompleted)="onStudioAgentCompleted($event)"
                    (viewChangeRequested)="onStudioViewChange($event)">
                </mkt-pipeline-composer>
            </div>

            <!-- ═══════════════ MARKETING DASHBOARD VIEW ═══════════════ -->
            <div *ngIf="activeView === 'marketing-dashboard'" class="cmo-view" style="padding: 0; overflow: hidden; height: 100%;">
                <mkt-marketing-dashboard-v2 [companyId]="defaultCompanyId"></mkt-marketing-dashboard-v2>
            </div>

            <!-- ═══════════════ SCHEDULED JOBS VIEW ═══════════════ -->
            <div *ngIf="activeView === 'jobs'" class="cmo-view">
                <div class="cmo-jobs-header">
                    <div>
                        <h3 class="cmo-jobs-title"><i class="fa-solid fa-clock-rotate-left"></i> Marketing Scheduled Jobs</h3>
                        <p class="cmo-jobs-subtitle">Trigger any marketing scheduled job to run immediately, bypassing its cron schedule.</p>
                    </div>
                    <button class="cmo-btn cmo-btn--sm" (click)="refreshScheduledJobs()" [disabled]="isLoading" title="Refresh">
                        <i class="fa-solid fa-arrows-rotate"></i> Refresh
                    </button>
                </div>

                <div *ngIf="scheduledJobs.length === 0" class="cmo-empty-queue">
                    <i class="fa-solid fa-calendar-xmark"></i>
                    <p>No marketing scheduled jobs found.</p>
                </div>

                <div *ngIf="scheduledJobs.length > 0" class="cmo-jobs-grid">
                    <div *ngFor="let job of scheduledJobs" class="cmo-job-card">
                        <div class="cmo-job-card__header">
                            <div class="cmo-job-card__title">
                                <i class="fa-solid fa-robot"></i>
                                <span>{{ job.Name }}</span>
                            </div>
                            <span class="cmo-badge"
                                  [class.cmo-badge--success]="job.Status === 'Active'"
                                  [class.cmo-badge--warning]="job.Status !== 'Active'">
                                {{ job.Status }}
                            </span>
                        </div>
                        <div *ngIf="job.Description" class="cmo-job-card__desc">{{ job.Description }}</div>
                        <div class="cmo-job-card__meta">
                            <div class="cmo-job-card__meta-item">
                                <span class="cmo-job-card__meta-label">Schedule</span>
                                <span class="cmo-job-card__meta-value cmo-mono">{{ job.CronExpression || '—' }}</span>
                            </div>
                            <div class="cmo-job-card__meta-item">
                                <span class="cmo-job-card__meta-label">Last Run</span>
                                <span class="cmo-job-card__meta-value">{{ formatJobDate(job.LastRunAt) }}</span>
                            </div>
                            <div class="cmo-job-card__meta-item">
                                <span class="cmo-job-card__meta-label">Next Run</span>
                                <span class="cmo-job-card__meta-value">{{ formatJobDate(job.NextRunAt) }}</span>
                            </div>
                        </div>
                        <div class="cmo-job-card__footer">
                            <button class="cmo-btn cmo-btn--sm cmo-btn--primary"
                                    [disabled]="job.Status !== 'Active' || jobRunState[job.ID] === 'running'"
                                    (click)="runScheduledJob(job)">
                                <i class="fa-solid"
                                   [class.fa-spinner]="jobRunState[job.ID] === 'running'"
                                   [class.fa-spin]="jobRunState[job.ID] === 'running'"
                                   [class.fa-play]="jobRunState[job.ID] !== 'running'"></i>
                                {{ jobRunState[job.ID] === 'running' ? 'Triggering…' : 'Run Now' }}
                            </button>
                            <span *ngIf="jobRunState[job.ID] === 'queued'" class="cmo-job-card__msg cmo-job-card__msg--ok">
                                <i class="fa-solid fa-check"></i> {{ jobRunMessage[job.ID] }}
                            </span>
                            <span *ngIf="jobRunState[job.ID] === 'error'" class="cmo-job-card__msg cmo-job-card__msg--err">
                                <i class="fa-solid fa-triangle-exclamation"></i> {{ jobRunMessage[job.ID] }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- ═══════════════ REVIEW QUEUE VIEW ═══════════════ -->
            <div *ngIf="activeView === 'review-queue'" class="cmo-view">

                <!-- KPI Bar -->
                <div class="cmo-kpi-bar">
                    <div class="cmo-kpi" [class.cmo-kpi--alert]="unprocessedSourceCount > 0" (click)="reviewFilter = 'sources'" style="cursor:pointer">
                        <span class="cmo-kpi__value">{{ unprocessedSourceCount }}</span>
                        <span class="cmo-kpi__label">Unprocessed</span>
                    </div>
                    <div class="cmo-kpi">
                        <span class="cmo-kpi__value">{{ blogs.length }}</span>
                        <span class="cmo-kpi__label">Posts</span>
                    </div>
                    <div class="cmo-kpi" [class.cmo-kpi--alert]="needsReviewCount > 0" (click)="reviewFilter = 'needs-review'" style="cursor:pointer">
                        <span class="cmo-kpi__value">{{ needsReviewCount }}</span>
                        <span class="cmo-kpi__label">Needs Review</span>
                    </div>
                    <div class="cmo-kpi">
                        <span class="cmo-kpi__value">{{ publishedCount }}</span>
                        <span class="cmo-kpi__label">Published</span>
                    </div>
                </div>

                <!-- Filter pills -->
                <div class="cmo-filter-pills">
                    <button class="cmo-pill" [class.cmo-pill--active]="reviewFilter === 'needs-review'" (click)="reviewFilter = 'needs-review'">
                        Needs Review ({{ needsReviewCount }})
                    </button>
                    <button class="cmo-pill" [class.cmo-pill--active]="reviewFilter === 'scheduled'" (click)="reviewFilter = 'scheduled'">
                        Ready to Publish ({{ scheduledCount }})
                    </button>
                    <button class="cmo-pill" [class.cmo-pill--active]="reviewFilter === 'all'" (click)="reviewFilter = 'all'">
                        All Content ({{ blogs.length }})
                    </button>
                    <button class="cmo-pill" [class.cmo-pill--active]="reviewFilter === 'sources'" (click)="reviewFilter = 'sources'">
                        <i class="fa-solid fa-podcast"></i> Sources ({{ contentSources.length }})
                    </button>
                    <button class="cmo-pill" [class.cmo-pill--active]="reviewFilter === 'newsletters'" (click)="reviewFilter = 'newsletters'">
                        <i class="fa-solid fa-envelope"></i> Newsletters ({{ newsletterCount }})
                        <span class="cmo-pill__alert" *ngIf="draftNewsletterCount > 0">{{ draftNewsletterCount }}</span>
                    </button>
                    <button class="cmo-pill" [class.cmo-pill--active]="reviewFilter === 'social-posts'" (click)="reviewFilter = 'social-posts'">
                        <i class="fa-solid fa-share-nodes"></i> Social Posts ({{ socialPostCount }})
                        <span class="cmo-pill__alert" *ngIf="draftSocialPostCount > 0">{{ draftSocialPostCount }}</span>
                    </button>
                    <button class="cmo-pill" [class.cmo-pill--active]="reviewFilter === 'experiments'" (click)="reviewFilter = 'experiments'">
                        <i class="fa-solid fa-flask"></i> Experiments ({{ experiments.length }})
                    </button>
                </div>

                <!-- Blog cards (needs-review / scheduled / all) -->
                <div class="cmo-queue-grid" *ngIf="reviewFilter === 'needs-review' || reviewFilter === 'scheduled' || reviewFilter === 'all'">
                    <div *ngIf="filteredBlogs.length === 0" class="cmo-empty-queue">
                        <i class="fa-solid fa-circle-check"></i>
                        <p>Nothing here — you're all caught up!</p>
                    </div>
                    <div *ngFor="let b of filteredBlogs" class="cmo-queue-card" (click)="openFlyout(b)">
                        <img *ngIf="b.FeaturedImageURL" [src]="b.FeaturedImageURL" class="cmo-queue-card__img" alt="" />
                        <div class="cmo-queue-card__body">
                            <div class="cmo-queue-card__title">{{ b.Title }}</div>
                            <div class="cmo-queue-card__meta">
                                <span class="cmo-badge" [ngClass]="'cmo-badge--' + getBlogStatusName(b.StatusID).toLowerCase()">
                                    {{ getBlogStatusName(b.StatusID) }}
                                </span>
                                <span *ngIf="getCalendarDateForBlog(b)" class="cmo-queue-card__date">
                                    <i class="fa-regular fa-calendar"></i> {{ formatDate(getCalendarDateForBlog(b)) }}
                                </span>
                                <span *ngIf="$any(b).CMSPostID" class="cmo-badge cmo-badge--hs">
                                    {{ $any(b).CMSPlatform || 'CMS' }}
                                </span>
                            </div>
                        </div>
                        <div class="cmo-queue-card__actions">
                            <button *ngIf="isBlogDraft(b)" class="cmo-btn cmo-btn--xs cmo-btn--primary" (click)="approveBlog(b); $event.stopPropagation()">
                                Approve
                            </button>
                            <button *ngIf="isBlogReady(b)" class="cmo-btn cmo-btn--xs cmo-btn--primary" (click)="publishBlog(b); $event.stopPropagation()">
                                Push to HubSpot
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Content Sources -->
                <div class="cmo-queue-grid" *ngIf="reviewFilter === 'sources'">
                    <div *ngIf="contentSources.length === 0" class="cmo-empty-queue">
                        <i class="fa-solid fa-inbox"></i>
                        <p>No content sources ingested yet.</p>
                    </div>
                    <div *ngFor="let cs of contentSources" class="cmo-queue-card cmo-queue-card--source">
                        <div class="cmo-queue-card__icon">
                            <i class="fa-solid" [ngClass]="cs.IsProcessed ? 'fa-circle-check' : 'fa-circle-dot'"
                               [style.color]="cs.IsProcessed ? 'var(--mj-color-success-400)' : 'var(--mj-status-warning)'"></i>
                        </div>
                        <div class="cmo-queue-card__body">
                            <div class="cmo-queue-card__title">{{ cs.Title || 'Untitled Source' }}</div>
                            <div class="cmo-queue-card__meta">
                                <span class="cmo-badge" [ngClass]="cs.IsProcessed ? 'cmo-badge--success' : 'cmo-badge--warning'">
                                    {{ cs.IsProcessed ? 'Processed' : 'Unprocessed' }}
                                </span>
                                <span *ngIf="cs.SourcePlatform" class="cmo-queue-card__date">
                                    {{ cs.SourcePlatform }}
                                </span>
                                <span class="cmo-queue-card__date">
                                    {{ formatDate(cs.__mj_CreatedAt) }}
                                </span>
                            </div>
                            <div *ngIf="cs.Summary" class="cmo-queue-card__summary">{{ cs.Summary | slice:0:150 }}{{ cs.Summary.length > 150 ? '...' : '' }}</div>
                        </div>
                        <div class="cmo-queue-card__actions">
                            <button class="cmo-btn cmo-btn--xs cmo-btn--danger" (click)="deleteContentSource(cs); $event.stopPropagation()" title="Delete source">
                                <i class="fa-solid fa-trash"></i>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Newsletters -->
                <div class="cmo-queue-grid" *ngIf="reviewFilter === 'newsletters'">
                    <div *ngIf="newsletters.length === 0" class="cmo-empty-queue">
                        <i class="fa-solid fa-envelope"></i>
                        <p>No newsletters yet. The Newsletter Agent will create them from published blog content.</p>
                    </div>
                    <div *ngFor="let nl of newsletters" class="cmo-queue-card cmo-queue-card--newsletter"
                         (click)="openNewsletterFlyout(nl)" style="cursor: pointer">
                        <div class="cmo-queue-card__icon">
                            <i class="fa-solid fa-envelope" style="color: var(--mj-color-info-500)"></i>
                        </div>
                        <div class="cmo-queue-card__body">
                            <div class="cmo-queue-card__title">{{ nl.Subject }}</div>
                            <div class="cmo-queue-card__meta">
                                <span class="cmo-badge" [class.cmo-badge--draft]="getContentStatusName(nl.ContentStatusID) === 'Draft'"
                                      [class.cmo-badge--approved]="getContentStatusName(nl.ContentStatusID) === 'Approved'">
                                    {{ getContentStatusName(nl.ContentStatusID) }}
                                </span>
                                <span *ngIf="nl.PreviewText" class="cmo-queue-card__date" style="font-style: italic">
                                    {{ nl.PreviewText.length > 60 ? nl.PreviewText.substring(0, 60) + '...' : nl.PreviewText }}
                                </span>
                                <span class="cmo-queue-card__date">
                                    {{ formatDate(nl.__mj_CreatedAt) }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Social Posts -->
                <div class="cmo-queue-grid" *ngIf="reviewFilter === 'social-posts'">
                    <div *ngIf="socialPosts.length === 0" class="cmo-empty-queue">
                        <i class="fa-solid fa-share-nodes"></i>
                        <p>No social posts yet. Use the Studio to generate them from published content.</p>
                    </div>
                    <div *ngFor="let sp of socialPosts" class="cmo-queue-card cmo-queue-card--social" (click)="openSocialPostFlyout(sp)" style="cursor: pointer;">
                        <div class="cmo-queue-card__icon">
                            <i class="fa-solid fa-share-nodes" style="color: var(--mj-color-violet-500)"></i>
                        </div>
                        <div class="cmo-queue-card__body">
                            <div class="cmo-queue-card__title">{{ sp.Body.length > 80 ? sp.Body.substring(0, 80) + '...' : sp.Body }}</div>
                            <div class="cmo-queue-card__meta">
                                <span class="cmo-badge" [class.cmo-badge--draft]="getContentStatusName(sp.ContentStatusID) === 'Draft'"
                                      [class.cmo-badge--approved]="getContentStatusName(sp.ContentStatusID) === 'Approved'"
                                      [class.cmo-badge--published]="getContentStatusName(sp.ContentStatusID) === 'Published'">
                                    {{ getContentStatusName(sp.ContentStatusID) }}
                                </span>
                                <span *ngIf="sp.SocialPlatform" class="cmo-badge cmo-badge--platform">
                                    {{ sp.SocialPlatform }}
                                </span>
                                <span *ngIf="sp.Blog" class="cmo-queue-card__date">
                                    <i class="fa-solid fa-link" style="font-size: 0.6rem"></i> {{ sp.Blog }}
                                </span>
                                <span class="cmo-queue-card__date">
                                    {{ formatDate(sp.__mj_CreatedAt) }}
                                </span>
                            </div>
                            <div class="cmo-queue-card__summary">{{ sp.Body.length > 200 ? sp.Body.substring(0, 200) + '...' : sp.Body }}</div>
                        </div>
                    </div>
                </div>

                <!-- Experiments -->
                <div class="cmo-queue-grid" *ngIf="reviewFilter === 'experiments'">
                    <div *ngIf="experiments.length === 0" class="cmo-empty-queue">
                        <i class="fa-solid fa-flask"></i>
                        <p>No experiments yet.</p>
                    </div>
                    <div *ngFor="let exp of experiments" class="cmo-queue-card cmo-queue-card--experiment">
                        <div class="cmo-queue-card__icon">
                            <i class="fa-solid fa-flask" style="color: var(--mj-color-violet-500)"></i>
                        </div>
                        <div class="cmo-queue-card__body">
                            <div class="cmo-queue-card__title">{{ exp.Hypothesis }}</div>
                            <div class="cmo-queue-card__meta">
                                <span class="cmo-badge">{{ $any(exp).ExperimentStatus || 'Unknown' }}</span>
                                <span *ngIf="$any(exp).ExperimentTier" class="cmo-queue-card__date">{{ $any(exp).ExperimentTier }}</span>
                                <span *ngIf="exp.DecisionDate" class="cmo-queue-card__date">
                                    Decision: {{ formatDate(exp.DecisionDate) }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Agent Sidebar -->
        <mkt-agent-sidebar
            [isRunning]="isRunning"
            [steps]="enhancedSteps"
            [elapsed]="getElapsed()"
            [collapsed]="sidebarCollapsed"
            [runOutputBlogs]="runOutputBlogs"
            [currentRunId]="currentRunId"
            (runRequested)="onRunRequested($event)"
            (collapsedChange)="onSidebarCollapse($event)">
        </mkt-agent-sidebar>
    </div>

    <!-- Content Flyout -->
    <div class="cmo-flyout-backdrop" *ngIf="flyout.open" (click)="closeFlyout()"></div>
    <div class="cmo-flyout" [class.cmo-flyout--open]="flyout.open">
        <mkt-blog-detail-panel
            *ngIf="flyout.open && flyoutBlog && !flyout.newsletterId"
            [blog]="flyoutBlog"
            publishLabel="Push to HubSpot"
            [allRevisions]="blogRevisions"
            (close)="closeFlyout()"
            (statusChange)="onFlyoutStatusChange($event)"
            (deleteBlog)="onFlyoutDeleteBlog($event)"
            (blogSaved)="onFlyoutBlogSaved($event)"
            (revisionRequested)="onFlyoutRevisionRequested($event)">
        </mkt-blog-detail-panel>
        <mkt-newsletter-detail-panel
            *ngIf="flyout.open && flyoutNewsletter && flyout.newsletterId"
            [newsletter]="flyoutNewsletter"
            [linkedBlogs]="flyoutNewsletterBlogs"
            [contentStatuses]="contentStatuses"
            (close)="closeFlyout()"
            (statusChange)="onNewsletterStatusChange($event)"
            (deleteNewsletter)="onNewsletterDelete($event)"
            (newsletterSaved)="onFlyoutBlogSaved($event)"
            (openBlog)="openBlogById($event)">
        </mkt-newsletter-detail-panel>
        <mkt-social-post-detail-panel
            *ngIf="flyout.open && flyoutSocialPost && flyout.socialPostId"
            [post]="flyoutSocialPost"
            [contentStatuses]="contentStatuses"
            (close)="closeFlyout()"
            (statusChange)="onSocialPostStatusChange($event)"
            (deletePost)="onSocialPostDelete($event)"
            (postSaved)="onFlyoutBlogSaved($event)">
        </mkt-social-post-detail-panel>
    </div>

    <!-- Note Popover -->
    <div class="cmo-note-popover" *ngIf="notePopover.open"
         [style.left.px]="notePopover.x" [style.top.px]="notePopover.y">
        <div class="cmo-note-popover__header">
            <span>Add Note for {{ formatDate(notePopover.date) }}</span>
            <button class="cmo-note-popover__close" (click)="closeNotePopover()"><i class="fa-solid fa-xmark"></i></button>
        </div>
        <div class="cmo-note-popover__body">
            <select [(ngModel)]="noteContentTypeId" class="cmo-select cmo-select--full">
                <option value="">Any type</option>
                <option *ngFor="let t of contentTypes" [value]="t.ID">{{ t.Name }}</option>
            </select>
            <input [(ngModel)]="noteTitle" class="cmo-input" placeholder="Brief title (e.g., Newsletter about Q1)" />
            <textarea [(ngModel)]="noteText" class="cmo-textarea" rows="3" placeholder="Instructions for the CMO agent..."></textarea>
            <div class="cmo-note-popover__actions">
                <button class="cmo-btn cmo-btn--sm" (click)="closeNotePopover()">Cancel</button>
                <button class="cmo-btn cmo-btn--sm cmo-btn--primary" (click)="saveNote()" [disabled]="!noteTitle">
                    <i class="fa-solid fa-plus"></i> Add Note
                </button>
            </div>
        </div>
    </div>

    <!-- Calendar Entry Edit Popover -->
    <div class="cmo-note-popover" *ngIf="editingCalendarEntry"
         style="left: 400px; top: 200px;">
        <div class="cmo-note-popover__header">
            <span>Edit Calendar Entry</span>
            <button class="cmo-note-popover__close" (click)="closeCalendarEntryEdit()"><i class="fa-solid fa-xmark"></i></button>
        </div>
        <div class="cmo-note-popover__body">
            <div style="margin-bottom: 4px;">
                <label class="cmo-field__label">Title</label>
                <input [(ngModel)]="editCalendarTitle" class="cmo-input" placeholder="Entry title" />
            </div>
            <div style="margin-bottom: 4px;">
                <label class="cmo-field__label">Date</label>
                <input type="date" [(ngModel)]="editCalendarDate" class="cmo-input" />
            </div>
            <div style="margin-bottom: 4px;">
                <label class="cmo-field__label">Notes (CMO instructions)</label>
                <textarea [(ngModel)]="editCalendarNotes" class="cmo-textarea" rows="3" placeholder="Instructions for the CMO agent..."></textarea>
            </div>
            <div class="cmo-note-popover__actions">
                <button class="cmo-btn cmo-btn--sm cmo-btn--danger" (click)="deleteCalendarEntry()">
                    <i class="fa-solid fa-trash"></i> Delete
                </button>
                <button class="cmo-btn cmo-btn--sm" (click)="closeCalendarEntryEdit()">Cancel</button>
                <button class="cmo-btn cmo-btn--sm cmo-btn--primary" (click)="saveCalendarEntryEdit()">
                    <i class="fa-solid fa-check"></i> Save
                </button>
            </div>
        </div>
    </div>

    <!-- Settings (full-page, replaces main layout) -->
    <mkt-settings-drawer *ngIf="settingsOpen && !isLoading"
        [open]="settingsOpen"
        [brandConfig]="brandConfigRaw"
        [strategy]="strategy"
        [strategyPillars]="strategyPillars"
        [contentPlan]="contentPlan"
        [contentPlanItems]="contentPlanItems"
        [channelConfigs]="channelConfigs"
        [contentTypes]="contentTypes"
        [socialPlatforms]="socialPlatforms"
        [integrations]="integrations"
        [companyIntegrations]="companyIntegrations"
        [contentFeeds]="contentFeeds"
        [contentTemplates]="contentTemplates"
        [saveStatus]="saveStatus"
        (openChange)="settingsOpen = $event"
        (saveBrandConfig)="saveBrandConfig()"
        (saveStrategy)="saveStrategy()"
        (savePillar)="savePillar($event)"
        (addPillar)="addPillar()"
        (deletePillar)="deletePillar($event.pillar, $event.index)"
        (savePlanItem)="savePlanItem($event)"
        (saveChannelConfig)="saveChannelConfig($event)"
        (addChannelConfig)="onAddChannelConfig($event)"
        (deleteChannelConfig)="onDeleteChannelConfig($event)"
        (saveFeed)="saveFeed($event)"
        (saveTemplate)="saveTemplate($event)"
        (createTemplate)="onCreateTemplate($event)"
        (addCompanyIntegration)="onAddCompanyIntegration($event)"
        (toggleCompanyIntegration)="onToggleCompanyIntegration($event)"
        (deleteCompanyIntegration)="onDeleteCompanyIntegration($event)">
    </mkt-settings-drawer>
</div>
